import { useContext } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import { LangContext } from '../App';

/**
 * ComingSoonPage — Composant générique pour les pages publiques "Coming Soon"
 *
 * Utilisé par Guides.jsx, Faune.jsx, Marche.jsx, Communaute.jsx. Charte
 * visuelle alignée sur Accueil.jsx (hero + grille de cartes + footer).
 *
 * Props :
 *   - title        { fr, en }            (requis) — title principal
 *   - sousTitre    { fr, en }            (optionnel) — accroche ambrée sous le title
 *   - description  { fr, en }            (requis) — paragraphe descriptif
 *   - cartes       [ { icon, title, description } ]  (requis, 1-4 cartes)
 *   - cta          { label, link, disabled? }         (optionnel)
 *   - noteLegale   { fr, en }            (optionnel) — texte italique sous le CTA
 *   - liensExtras  [ { label, link } ]   (optionnel) — liens secondaires
 *
 * Tous les textes traduisibles sont des objets { fr, en } parsés selon
 * LangContext.
 */

const footerLinkStyle = {
  color: 'rgba(255,255,255,0.42)',
  textDecoration: 'none',
  fontSize: '0.82rem',
  transition: 'color 0.15s',
};

const FOOTER_T = {
  fr: {
    slogan: 'En territoire de chasse responsable',
    colPlateforme: 'Plateforme',
    colSupport: 'Support',
    confidentialite: 'Confidentialité',
    copy: '© 2025 ChasseHunt Inc. — chassehunt.ca / chassehunt.com',
    bientotBadge: 'Bientôt available',
  },
  en: {
    slogan: 'In responsible hunting territory',
    colPlateforme: 'Platform',
    colSupport: 'Support',
    confidentialite: 'Privacy',
    copy: '© 2025 ChasseHunt Inc. — chassehunt.ca / chassehunt.com',
    bientotBadge: 'Coming soon',
  },
};

const NAV_T = {
  fr: {
    lands: 'Terres',
    guides: 'Guides',
    faune: 'Faune',
    marche: 'Marché',
    communaute: 'Communauté',
  },
  en: {
    lands: 'Lands',
    guides: 'Guides',
    faune: 'Wildlife',
    marche: 'Market',
    communaute: 'Community',
  },
};

export default function ComingSoonPage({
  title,
  sousTitre,
  description,
  cartes,
  cta,
  noteLegale,
  liensExtras,
}) {
  const { lang } = useContext(LangContext);
  const fT = FOOTER_T[lang];
  const nT = NAV_T[lang];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--couleur-fond)',
        color: 'var(--couleur-texte)',
      }}
    >
      <Header />

      {/* ── Hero ── */}
      <section
        style={{
          padding: '5rem 2rem 4rem',
          textAlign: 'center',
          background:
            'linear-gradient(180deg, var(--couleur-fond-haut) 0%, var(--couleur-fond) 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 50% -10%, rgba(201,162,39,0.09) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />

        <div style={{ maxWidth: '700px', margin: '0 auto', position: 'relative' }}>
          {/* Badge "Bientôt available" */}
          <div
            style={{
              display: 'inline-block',
              background: 'rgba(201,162,39,0.12)',
              border: '1px solid rgba(201,162,39,0.35)',
              color: 'var(--couleur-or)',
              padding: '0.3rem 0.85rem',
              borderRadius: '20px',
              fontSize: '0.72rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontWeight: 700,
              marginBottom: '1.5rem',
            }}
          >
            {fT.bientotBadge}
          </div>

          <h1
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
              color: '#fff',
              margin: '0 0 1rem',
              lineHeight: 1.25,
              textShadow: '0 2px 14px rgba(0,0,0,0.6)',
            }}
          >
            {title[lang]}
          </h1>

          {sousTitre && (
            <p
              style={{
                fontSize: '1.05rem',
                color: 'var(--couleur-or)',
                margin: '0 0 1.2rem',
                fontWeight: 500,
              }}
            >
              {sousTitre[lang]}
            </p>
          )}

          <p
            style={{
              fontSize: '1rem',
              color: 'rgba(255,255,255,0.65)',
              margin: 0,
              lineHeight: 1.7,
              maxWidth: '580px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            {description[lang]}
          </p>
        </div>
      </section>

      {/* ── Cartes ── */}
      <section style={{ padding: '3.5rem 2rem 2rem', background: 'var(--couleur-fond)' }}>
        <div style={{ maxWidth: '940px', margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1.5rem',
            }}
          >
            {cartes.map((c, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(201,162,39,0.1)',
                  borderRadius: '10px',
                  padding: '1.8rem 1.5rem',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '2.2rem', marginBottom: '1rem', opacity: 0.85 }}>
                  {c.icon}
                </div>
                <h3
                  style={{
                    color: 'var(--couleur-texte)',
                    fontSize: '1rem',
                    margin: '0 0 0.7rem',
                    fontWeight: 600,
                  }}
                >
                  {c.title[lang]}
                </h3>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: '0.86rem',
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  {c.description[lang]}
                </p>
              </div>
            ))}
          </div>

          {/* Liens extras */}
          {liensExtras && liensExtras.length > 0 && (
            <div
              style={{
                textAlign: 'center',
                marginTop: '2.5rem',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                justifyContent: 'center',
              }}
            >
              {liensExtras.map((l, i) => (
                <Link
                  key={i}
                  to={l.link}
                  style={{
                    color: 'var(--couleur-or)',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem',
                    borderBottom: '1px solid rgba(201,162,39,0.3)',
                  }}
                >
                  {l.label[lang]} →
                </Link>
              ))}
            </div>
          )}

          {/* CTA */}
          {cta && (
            <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
              {cta.disabled ? (
                <button
                  disabled
                  style={{
                    background: 'rgba(201,162,39,0.3)',
                    color: 'rgba(10,26,10,0.6)',
                    border: 'none',
                    padding: '0.85rem 2.2rem',
                    borderRadius: '6px',
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: 'not-allowed',
                  }}
                >
                  {cta.label[lang]}
                </button>
              ) : (
                <Link
                  to={cta.link}
                  style={{
                    display: 'inline-block',
                    background: 'var(--couleur-or)',
                    color: 'var(--couleur-fond-carte)',
                    padding: '0.85rem 2.2rem',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: 700,
                    fontSize: '1rem',
                    boxShadow: '0 4px 16px rgba(201,162,39,0.35)',
                  }}
                >
                  {cta.label[lang]}
                </Link>
              )}
            </div>
          )}

          {/* Note légale */}
          {noteLegale && (
            <p
              style={{
                textAlign: 'center',
                marginTop: '2rem',
                fontSize: '0.78rem',
                color: 'rgba(255,255,255,0.4)',
                fontStyle: 'italic',
                maxWidth: '620px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              {noteLegale[lang]}
            </p>
          )}
        </div>
      </section>

      {/* ── Footer simplifié (sans liens cassés) ── */}
      <footer
        style={{
          background: 'var(--couleur-fond-pied)',
          borderTop: '1px solid rgba(201,162,39,0.12)',
          padding: '3rem 2rem 2rem',
          marginTop: '3rem',
        }}
      >
        <div style={{ maxWidth: '980px', margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '2rem',
              marginBottom: '2.5rem',
            }}
          >
            <div>
              <div
                style={{
                  color: 'var(--couleur-or)',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  marginBottom: '0.9rem',
                  letterSpacing: '0.04em',
                }}
              >
                {fT.colPlateforme}
              </div>
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                <li>
                  <Link to="/lands" style={footerLinkStyle}>
                    {nT.lands}
                  </Link>
                </li>
                <li>
                  <Link to="/guides" style={footerLinkStyle}>
                    {nT.guides}
                  </Link>
                </li>
                <li>
                  <Link to="/wildlife" style={footerLinkStyle}>
                    {nT.faune}
                  </Link>
                </li>
                <li>
                  <Link to="/market" style={footerLinkStyle}>
                    {nT.marche}
                  </Link>
                </li>
                <li>
                  <Link to="/community" style={footerLinkStyle}>
                    {nT.communaute}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <div
                style={{
                  color: 'var(--couleur-or)',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  marginBottom: '0.9rem',
                  letterSpacing: '0.04em',
                }}
              >
                {fT.colSupport}
              </div>
              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                <li>
                  <Link to="/privacy-policy" style={footerLinkStyle}>
                    {fT.confidentialite}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.07)',
              paddingTop: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: '1.1rem',
                color: 'var(--couleur-or)',
                letterSpacing: '0.04em',
              }}
            >
              ChasseHunt
            </span>
            <p
              style={{
                color: 'rgba(255,255,255,0.3)',
                fontSize: '0.75rem',
                fontStyle: 'italic',
                margin: 0,
              }}
            >
              {fT.slogan}
            </p>
            <p
              style={{
                color: 'rgba(255,255,255,0.2)',
                fontSize: '0.72rem',
                margin: 0,
              }}
            >
              {fT.copy}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
